import {useCallback, useContext, useEffect, useState} from 'react';
import {useDispatch} from 'react-redux';
import {useLocation, useNavigate} from 'react-router-dom';
import {useMutation, useQuery} from 'react-query';
import {googleSignin, login} from '../requests';
import {onSignupSuccessHelper} from '../helpers';
import {toast} from 'react-toastify';
import * as EmailValidator from 'email-validator';
import {encryptData} from '../helpers/encryptData';
import {getLocation} from '../store/actions';
import {logo} from '../assets/images/images';
import {LogoContext} from '../helpers/context';

export const useSignInLogic = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const subLogo = useContext(LogoContext);
  const [passwordShown, setPasswordShown] = useState(false);
  const [loading, setLoading] = useState(false);
  const [validate2FA, setValidate2FA] = useState(false);
  const [errors, setErrors] = useState({});
  const [inputs, setInputValue] = useState({
    email: '',
    password: '',
  });

  const location = useLocation();

  useEffect(() => {
    dispatch(getLocation(location));
  }, [dispatch, location]);

  const {
    isLoading: sLoading,
    mutate,
    data,
  } = useMutation(data => login(data), {
    onSuccess: data => {
      data?.two_factor_auth
        ? setValidate2FA(data?.two_factor_auth)
        : onSignupSuccessHelper(data, dispatch, navigate, location);
    },

    onError: error => {
      if (error.response?.data?.error === 'JsonWebTokenError: jwt malformed') {
        toast.error('Internal Server Error');
        return;
      }
      toast.error(error.response?.data?.error || error.message);
    },
  });

  const {refetch} = useQuery('google-signup', () => googleSignin(), {
    enabled: false,
    onSuccess: data => {
      setLoading(false);
      const {url} = data;
      if (url) {
        window.location.href = url;
      }
    },
    onError: error => {
      setLoading(false);
      toast.error(error.message || 'Google sign-in failed');
    },
  });

  const handleInputChange = e => {
    setInputValue({...inputs, [e.target.name]: e.target.value});
    setErrors({});
  };

  const handleGoogleSignin = () => {
    setLoading(true);
    const timeOut = setTimeout(() => {
      refetch().then(r => r);
      setLoading(false);
    }, 1000);

    return () => {
      clearTimeout(timeOut);
    };
  };
  const companyLogo = localStorage.getItem('companyLogo') || subLogo || logo;

  const checkErrors = useCallback(() => {
    const {email, password} = inputs;
    if (email && !EmailValidator.validate(email))
      errors.email = 'Email is not valid';
    if (!email) errors.email = 'Email is required';
    if (!password.length) errors.password = 'Password is required';
    setErrors(errors);
  }, [errors, inputs]);

  const handleSubmit = async e => {
    e.preventDefault();
    checkErrors();
    if (Object.keys(errors).length === 0) {
      try {
        const encryptedLoginData = encryptData(JSON.stringify(inputs));
        await mutate({payload: encryptedLoginData});
      } catch (error) {
        console.log(error.response?.data?.error);
      }
    } else {
      Object.keys(errors).forEach(key => toast.error(errors[key]));
    }
  };
  const togglePassword = () => {
    setPasswordShown(!passwordShown);
  };
  return {
    togglePassword,
    loading,
    handleSubmit,
    handleGoogleSignin,
    handleInputChange,
    data,
    validate2FA,
    sLoading,
    dispatch,
    navigate,
    location,
    passwordShown,
    inputs,
    companyLogo,
  };
};
